import { useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, AlertTriangle, User, HelpCircle, Bug, Send, CheckCircle, Upload, Image, Video, Trash2 } from 'lucide-react';
import { userReportsAPI } from '@/utils/api';
import { toast } from 'sonner';

const REPORT_TYPES = [
  {
    id: 'user',
    label: 'Report User',
    description: 'Report inappropriate behavior, harassment, or rule violations',
    icon: AlertTriangle,
    color: 'text-red-400',
    bgColor: 'bg-red-500/20',
    borderColor: 'border-red-500/50'
  },
  {
    id: 'account',
    label: 'Account Issue',
    description: 'Problems with your account, login, purchases, or settings',
    icon: User,
    color: 'text-orange-400',
    bgColor: 'bg-orange-500/20',
    borderColor: 'border-orange-500/50'
  },
  {
    id: 'bug',
    label: 'Bug Report',
    description: 'Report technical issues, glitches, or broken features',
    icon: Bug,
    color: 'text-blue-400',
    bgColor: 'bg-blue-500/20',
    borderColor: 'border-blue-500/50'
  }
];

export const ReportModal = ({ 
  isOpen, 
  onClose, 
  // For pre-filling user report
  reportedUser = null 
}) => {
  const [step, setStep] = useState(reportedUser ? 2 : 1); // Skip type selection if reporting a user
  const [reportType, setReportType] = useState(reportedUser ? 'user' : null);
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [reportedUsername, setReportedUsername] = useState(reportedUser?.username || '');
  const [attachment, setAttachment] = useState(null);
  const [attachmentPreview, setAttachmentPreview] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 10MB');
      return;
    }

    // Check file type
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'video/mp4', 'video/webm', 'video/quicktime'];
    if (!validTypes.includes(file.type)) {
      toast.error('Invalid file type. Please upload an image or video');
      return;
    }

    setAttachment(file);

    // Create preview for images
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => setAttachmentPreview({ type: 'image', url: e.target.result });
      reader.readAsDataURL(file);
    } else {
      setAttachmentPreview({ type: 'video', name: file.name });
    }
  };

  const removeAttachment = () => {
    setAttachment(null);
    setAttachmentPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleClose = () => {
    // Reset state
    setStep(reportedUser ? 2 : 1);
    setReportType(reportedUser ? 'user' : null);
    setSubject('');
    setDescription('');
    setReportedUsername(reportedUser?.username || '');
    setAttachment(null);
    setAttachmentPreview(null);
    setIsSuccess(false);
    onClose();
  };

  const handleSelectType = (type) => {
    setReportType(type);
    setStep(2);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!subject.trim() || !description.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }

    // For user reports without pre-selected user, require username
    if (reportType === 'user' && !reportedUser && !reportedUsername.trim()) {
      toast.error('Please enter the username of the user you are reporting');
      return;
    }

    setIsSubmitting(true);
    try {
      // Convert attachment to base64 if present
      let attachmentData = null;
      if (attachment) {
        const reader = new FileReader();
        attachmentData = await new Promise((resolve, reject) => {
          reader.onload = () => resolve({
            data: reader.result,
            filename: attachment.name,
            content_type: attachment.type
          });
          reader.onerror = reject;
          reader.readAsDataURL(attachment);
        });
      }

      await userReportsAPI.create({
        report_type: reportType,
        subject: subject.trim(),
        description: description.trim(),
        reported_user_id: reportedUser?.id || null,
        reported_username: reportedUser?.username || (reportType === 'user' ? reportedUsername.trim() : null),
        attachment: attachmentData
      });
      
      setIsSuccess(true);
      setTimeout(() => {
        handleClose();
      }, 2000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit report');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const selectedType = REPORT_TYPES.find(t => t.id === reportType);

  // Use portal to render modal at document body level, escaping any backdrop-filter containing blocks
  return createPortal(
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/80 z-[10000] overflow-y-auto"
        onClick={handleClose}
        style={{
          paddingTop: 'calc(env(safe-area-inset-top, 16px) + 1rem)',
          paddingBottom: 'calc(env(safe-area-inset-bottom, 16px) + 1rem)',
          paddingLeft: 'env(safe-area-inset-left, 0px)',
          paddingRight: 'env(safe-area-inset-right, 0px)',
        }}
      >
        <div className="min-h-full flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="bg-card border border-white/10 rounded-xl w-full max-w-lg max-h-[80vh] overflow-y-auto my-auto"
            onClick={e => e.stopPropagation()}
          >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-white/10">
            <h2 className="text-xl font-heading uppercase text-white">
              {isSuccess ? 'Report Submitted' : 'Submit a Report'}
            </h2>
            <button
              onClick={handleClose}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-400" />
            </button>
          </div>

          {/* Success State */}
          {isSuccess ? (
            <div className="p-8 text-center">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className="w-20 h-20 mx-auto mb-4 rounded-full bg-green-500/20 flex items-center justify-center"
              >
                <CheckCircle className="w-10 h-10 text-green-400" />
              </motion.div>
              <h3 className="text-xl font-bold text-white mb-2">Thank You!</h3>
              <p className="text-gray-400">
                Your report has been submitted. The owner will review it shortly.
              </p>
            </div>
          ) : (
            <>
              {/* Step 1: Select Report Type */}
              {step === 1 && (
                <div className="p-4">
                  <p className="text-gray-400 mb-4">What would you like to report?</p>
                  <div className="space-y-3">
                    {REPORT_TYPES.map((type) => (
                      <button
                        key={type.id}
                        onClick={() => handleSelectType(type.id)}
                        className={`w-full p-4 rounded-lg border ${type.borderColor} ${type.bgColor} hover:bg-opacity-30 transition-all text-left group`}
                      >
                        <div className="flex items-start gap-3">
                          <type.icon className={`w-6 h-6 ${type.color} flex-shrink-0 mt-0.5`} />
                          <div>
                            <h3 className="font-bold text-white group-hover:text-primary transition-colors">
                              {type.label}
                            </h3>
                            <p className="text-sm text-gray-400 mt-1">{type.description}</p>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Step 2: Report Form */}
              {step === 2 && selectedType && (
                <form onSubmit={handleSubmit} className="p-4">
                  {/* Type indicator */}
                  <div className={`flex items-center gap-2 p-3 rounded-lg ${selectedType.bgColor} border ${selectedType.borderColor} mb-4`}>
                    <selectedType.icon className={`w-5 h-5 ${selectedType.color}`} />
                    <span className={`font-bold ${selectedType.color}`}>{selectedType.label}</span>
                    {!reportedUser && (
                      <button
                        type="button"
                        onClick={() => { setStep(1); setReportType(null); }}
                        className="ml-auto text-xs text-gray-400 hover:text-white"
                      >
                        Change
                      </button>
                    )}
                  </div>

                  {/* Reported User (if applicable) */}
                  {reportedUser && (
                    <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                      <p className="text-xs text-gray-400 mb-1">Reporting User</p>
                      <p className="text-white font-bold">{reportedUser.username}</p>
                    </div>
                  )}

                  {/* Username input for user reports without pre-selected user */}
                  {reportType === 'user' && !reportedUser && (
                    <div className="mb-4">
                      <label className="block text-xs uppercase tracking-widest font-bold text-gray-400 mb-2">
                        Username to Report *
                      </label>
                      <input
                        type="text"
                        value={reportedUsername}
                        onChange={(e) => setReportedUsername(e.target.value)}
                        placeholder="Enter the username of the person you're reporting"
                        className="w-full bg-input border border-red-500/30 focus:border-red-500 rounded-lg px-4 py-3 text-white placeholder-gray-500 outline-none"
                        maxLength={50}
                        required
                      />
                    </div>
                  )}

                  {/* Subject */}
                  <div className="mb-4">
                    <label className="block text-xs uppercase tracking-widest font-bold text-gray-400 mb-2">
                      Subject *
                    </label>
                    <input
                      type="text"
                      value={subject}
                      onChange={(e) => setSubject(e.target.value)}
                      placeholder={
                        reportType === 'user' ? 'e.g., Harassment in chat' :
                        reportType === 'account' ? 'e.g., Cannot access premium features' :
                        'e.g., Video player crashes on mobile'
                      }
                      className="w-full bg-input border border-border focus:border-primary rounded-lg px-4 py-3 text-white placeholder-gray-500 outline-none"
                      maxLength={100}
                      required
                    />
                  </div>

                  {/* Description */}
                  <div className="mb-4">
                    <label className="block text-xs uppercase tracking-widest font-bold text-gray-400 mb-2">
                      Description *
                    </label>
                    <textarea
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder="Please provide as much detail as possible..."
                      rows={4}
                      className="w-full bg-input border border-border focus:border-primary rounded-lg px-4 py-3 text-white placeholder-gray-500 outline-none resize-none"
                      maxLength={2000}
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1 text-right">
                      {description.length}/2000
                    </p>
                  </div>

                  {/* Attachment Upload */}
                  <div className="mb-6">
                    <label className="block text-xs uppercase tracking-widest font-bold text-gray-400 mb-2">
                      Attachment (Optional)
                    </label>
                    
                    {!attachment ? (
                      <div
                        onClick={() => fileInputRef.current?.click()}
                        className="border-2 border-dashed border-white/20 hover:border-primary/50 rounded-lg p-4 text-center cursor-pointer transition-colors"
                      >
                        <Upload className="w-8 h-8 mx-auto text-gray-500 mb-2" />
                        <p className="text-sm text-gray-400">
                          Click to upload image or video
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          Max 10MB • PNG, JPG, GIF, WebP, MP4, WebM
                        </p>
                      </div>
                    ) : (
                      <div className="relative border border-white/20 rounded-lg p-3">
                        {attachmentPreview?.type === 'image' ? (
                          <div className="relative">
                            <img
                              src={attachmentPreview.url}
                              alt="Attachment preview"
                              className="w-full h-32 object-cover rounded-lg"
                            />
                            <div className="absolute top-2 left-2 bg-black/70 px-2 py-1 rounded text-xs text-white flex items-center gap-1">
                              <Image className="w-3 h-3" />
                              Image
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-center gap-3 p-2">
                            <div className="w-12 h-12 bg-primary/20 rounded-lg flex items-center justify-center">
                              <Video className="w-6 h-6 text-primary" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm text-white truncate">{attachmentPreview?.name}</p>
                              <p className="text-xs text-gray-500">Video file</p>
                            </div>
                          </div>
                        )}
                        <button
                          type="button"
                          onClick={removeAttachment}
                          className="absolute top-2 right-2 p-1.5 bg-red-500/80 hover:bg-red-500 rounded-lg transition-colors"
                        >
                          <Trash2 className="w-4 h-4 text-white" />
                        </button>
                      </div>
                    )}
                    
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/gif,image/webp,video/mp4,video/webm,video/quicktime"
                      onChange={handleFileSelect}
                      className="hidden"
                    />
                  </div>

                  {/* Submit Button */}
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={handleClose}
                      className="flex-1 bg-white/10 hover:bg-white/20 text-white px-4 py-3 rounded-lg font-bold text-sm transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmitting || !subject.trim() || !description.trim()}
                      className="flex-1 bg-primary hover:bg-primary/80 disabled:bg-primary/50 disabled:cursor-not-allowed text-white px-4 py-3 rounded-lg font-bold text-sm transition-colors flex items-center justify-center gap-2"
                    >
                      {isSubmitting ? (
                        <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      ) : (
                        <>
                          <Send className="w-4 h-4" />
                          Submit Report
                        </>
                      )}
                    </button>
                  </div>
                </form>
              )}
            </>
          )}
        </motion.div>
        </div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
};

export default ReportModal;
